import { createSelector, Selector } from '@reduxjs/toolkit'
import { Collateral, CollateralBasket, EntityType, Instrument, OrderDetail } from 'api'
import { GlobalState } from 'store'
import { InstrumentHelper } from 'model'
import { compareInsensitive, Nullable } from 'utils'
import { createEntityGetterSelector, createEntitySelector } from '../entitySelectors'
import { createCollateralProductSectorIdSelector } from '../collateralSelectors'
import { createCollateralBasketProductSectorIdSelector } from '../collateralBasketSelectors'
import { pipe } from 'lodash/fp'
import { createFilteredInstrumentsSelector } from './instrumentFilterSelectors'
import { createInstrumentFiltersForFundSelector } from './instrumentFiltersForFundSelector'
import { createInstrumentFiltersForStagingCollateralSelector } from './instrumentFiltersForStagingInstrumentSelector'

const NULL_SELECTOR = () => null

const sortInstruments = (instruments: Instrument[]) =>
  instruments.sort((inst1, inst2) => compareInsensitive(InstrumentHelper.getName(inst1), InstrumentHelper.getName(inst2)))

const mapInstrumentToIds = (instruments: Instrument[]) => instruments.map(instrument => instrument.id)

export const sortAndMapInstruments = pipe(sortInstruments, mapInstrumentToIds)

export const createInstrumentListForInstrumentFiltersSelector = <TProps>(
  instrumentFiltersSelector: Selector<{}, Array<(instrument: Instrument) => boolean>, [TProps]>
) =>
  createSelector(createFilteredInstrumentsSelector(instrumentFiltersSelector), instruments =>
    pipe(sortInstruments, mapInstrumentToIds)(instruments)
  )

export const createInstrumentIdListSelector = <TProps>(fundIdSelector: Selector<{}, null | string | undefined, [TProps]> = NULL_SELECTOR) =>
  createInstrumentListForInstrumentFiltersSelector(createInstrumentFiltersForFundSelector(fundIdSelector))

export const createInstrumentListForStagingCollateralSelector = <TProps>(orderDetailSelector: Selector<{}, OrderDetail, [TProps]>) =>
  createInstrumentListForInstrumentFiltersSelector(createInstrumentFiltersForStagingCollateralSelector(orderDetailSelector))

export const createInstrumentGetterSelector: Selector<
  GlobalState,
  (instrumentId: string) => Instrument | null
> = createSelector(
  createEntityGetterSelector(EntityType.COLLATERAL),
  createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
  (getCollateral, getBasket) => (instrumentId: string) => getCollateral(instrumentId) ?? getBasket(instrumentId)
)

export const createCollateralsFromInstrumentIdGetterSelector: () => Selector<
  GlobalState,
  (instrumentId: string) => { collateral: Nullable<Collateral>; collateralBasket: Nullable<CollateralBasket> }
> = () =>
  createSelector(
    createEntityGetterSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    (getCollateral, getBasket) => (instrumentId: string) => ({
      collateral: getCollateral(instrumentId),
      collateralBasket: getBasket(instrumentId)
    })
  )

export function createInstrumentSelector<TProps>(
  instrumentIdSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, Instrument | null | undefined, [TProps]> {
  return createSelector(
    createEntitySelector(instrumentIdSelector, EntityType.COLLATERAL),
    createEntitySelector(instrumentIdSelector, EntityType.COLLATERAL_BASKET),
    (collateral, basket) => collateral ?? basket
  )
}

export function createIsInstrumentCollateralBasketSelector<TProps>(
  instrumentIdSelector: Selector<{}, string | null | undefined, [TProps]>
) {
  return createSelector(createEntitySelector(instrumentIdSelector, EntityType.COLLATERAL_BASKET), basket => !!basket)
}

export function createInstrumentProductSectorIdSelector<TProps>(instrumentIdSelector: Selector<{}, string | null | undefined, [TProps]>) {
  return createSelector(
    [createCollateralProductSectorIdSelector(instrumentIdSelector), createCollateralBasketProductSectorIdSelector(instrumentIdSelector)],
    (collateralProductSectorId, basketProductSectorId) => collateralProductSectorId ?? basketProductSectorId
  )
}
